<template>
  <v-card
    v-if="isSupported"
    class="rounded-lg fill-height"
  >
    <v-card-title class="pa-4 d-flex justify-space-between align-center">
      <div class="subtitle-2 grey--text text--grey-100">
        {{ chartTitle }}
      </div>
      <div class="d-flex align-center">
        <v-select
          v-if="tagOptions.length > 5"
          v-model="selectedTagId"
          :items="tagOptions"
          item-value="id"
          item-text="label"
          dense
          outlined
          hide-details
          style="max-width: 180px;"
          class="caption"
          @change="$emit('tag-change', $event)"
        />
        <v-btn-toggle
          v-else-if="tagOptions.length > 0"
          v-model="selectedTagId"
          mandatory
          dense
          color="primary"
        >
          <v-btn
            v-for="tag in tagOptions"
            :key="tag.id"
            :value="tag.id"
            x-small
            text
            class="text-none"
            @click="$emit('tag-change', tag.id)"
          >
            {{ tag.label }}
          </v-btn>
        </v-btn-toggle>
        <v-btn
          v-if="removable"
          icon
          style="pointer-events: auto"
          @click="$emit('remove')"
        >
          <v-icon small>
            fal fa-times
          </v-icon>
        </v-btn>
      </div>
    </v-card-title>
    <v-card-text>
      <apexchart
        v-if="series.length > 0"
        type="line"
        :height="chartHeight"
        :options="chartOptions"
        :series="series"
      />
      <div
        v-else
        class="d-flex justify-center align-center"
        style="height: 200px"
      >
        <span class="grey--text">{{ $t('coach_insights.no_data') }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { getTranslatedTagValue } from '@/utils/helpers'
import dayjs from 'dayjs'

const TOTAL_TAG_ID = '__total__'
const SUPPORTED_KEYS = ['EXERCISE_INSIGHTS_TREND']

export default {
  name: 'ExerciseInsightsTrendChart',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    chart: {
      type: Object,
      required: true
    },
    trendData: {
      type: Array,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    },
    height: {
      type: [String, Number],
      default: 350
    },
    removable: {
      type: Boolean,
      default: false
    },
    initialTag: {
      type: String,
      default: TOTAL_TAG_ID
    }
  },
  data() {
    return {
      selectedTagId: this.initialTag || TOTAL_TAG_ID
    }
  },
  computed: {
    isSupported() {
      return this.chart && SUPPORTED_KEYS.includes(this.chart.key)
    },
    chartVariable() {
      if (!this.chart || !this.chart.variable || !this.chart.category) return null
      return this.variables.find(v =>
        v.value === this.chart.variable && v.category === this.chart.category
      ) || null
    },
    chartTitle() {
      const base = this.chartVariable
        ? (this.chartVariable.long_text || this.chartVariable.text || this.chart.variable)
        : this.$t('coach_insights.chart.title')
      return `${base} ${this.$t('insights.trend_suffix')}`
    },
    availableTags() {
      if (!this.chartVariable || !this.chartVariable.suffix_agg) return []
      return Object.keys(this.chartVariable.suffix_agg).map(tagId => ({
        id: tagId,
        label: tagId === 'group_id' ? this.$t('insights.by_group') : tagId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      }))
    },
    tagOptions() {
      return [
        { id: TOTAL_TAG_ID, label: this.$t('coach_insights.chart.total') },
        ...this.availableTags
      ]
    },
    isTotal() {
      return this.selectedTagId === TOTAL_TAG_ID
    },
    statPrefix() {
      if (!this.chart || !this.chart.category || !this.chart.variable) return ''
      return `${this.chart.category.toLowerCase()}.${this.chart.variable}`
    },
    chartHeight() {
      return this.height
    },
    dateLabels() {
      return this.trendData.map(item => dayjs(item.date).format('MMM D'))
    },
    series() {
      if (!this.statPrefix || this.trendData.length === 0) return []

      if (this.isTotal) {
        return [{
          name: this.chartTitle,
          data: this.trendData.map(item => {
            const val = item.stats[this.statPrefix]
            if (typeof val === 'number') return Math.round(val * 100) / 100
            if (val && typeof val === 'object' && typeof val.total === 'number') return Math.round(val.total * 100) / 100
            return 0
          })
        }]
      }

      const tagStatsKey = `${this.statPrefix}_${this.selectedTagId}`
      const tagValues = new Set()

      this.trendData.forEach(item => {
        const data = item.stats[tagStatsKey]
        if (data && typeof data === 'object') {
          Object.keys(data).forEach(v => tagValues.add(v))
        }
      })

      return Array.from(tagValues).map(value => ({
        name: getTranslatedTagValue(value),
        data: this.trendData.map(item => {
          const data = item.stats[tagStatsKey] || {}
          const val = data[value] || 0
          if (typeof val === 'number') return Math.round(val * 100) / 100
          if (val && typeof val === 'object' && typeof val.total === 'number') return Math.round(val.total * 100) / 100
          return 0
        })
      }))
    },
    chartOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        xaxis: {
          categories: this.dateLabels,
          labels: {
            rotate: -45,
            rotateAlways: this.dateLabels.length > 14
          }
        },
        yaxis: {
          min: 0
        },
        colors: ['#1976D2', '#388E3C', '#F57C00', '#D32F2F', '#7B1FA2', '#0097A7', '#5D4037', '#455A64', '#C2185B', '#00796B', '#FFA000', '#512DA8'],
        legend: {
          position: 'top',
          show: !this.isTotal
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          x: {
            show: true
          }
        },
        markers: {
          size: 3,
          hover: { size: 5 }
        }
      }
    }
  }
}
</script>
